/**
 * Hero Slider - WCAG 2.1 AA Compliant
 * Handles hero slide transitions, navigation, autoplay, and accessibility
 * 
 * Features:
 * - Full keyboard navigation
 * - Screen reader announcements
 * - Reduced motion support
 * - Intersection Observer for performance
 * - Touch/swipe support
 * - Error handling
 */

class HeroSlider {
  constructor(element) {
    try {
      this.slider = element;
      this.track = element.querySelector('[data-slider-track]');
      this.slides = Array.from(element.querySelectorAll('.hero-slider__slide'));
      this.prevButton = element.querySelector('[data-slider-prev]');
      this.nextButton = element.querySelector('[data-slider-next]');
      this.indicators = Array.from(element.querySelectorAll('[data-slider-indicator]'));
      this.announcement = document.querySelector('[data-slider-announcement]');
      
      this.currentIndex = 0;
      this.autoplay = element.dataset.autoplay === 'true';
      this.speed = parseInt(element.dataset.speed) * 1000 || 5000;
      this.transitionSpeed = parseInt(element.dataset.transitionSpeed) || 500;
      this.autoplayTimer = null;
      this.isTransitioning = false;
      this.touchStartX = 0;
      this.touchEndX = 0;
      this.observer = null;
      
      // Check for reduced motion preference
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      if (this.slides.length > 1) {
        this.init();
      }
    } catch (error) {
      console.error('HeroSlider initialization error:', error);
    }
  }

  init() {
    try {
      // Set up Intersection Observer for performance
      this.setupIntersectionObserver();

      // Bind event listeners
      this.bindEvents();

      // Set initial ARIA attributes
      this.updateARIA();

      // Start autoplay if enabled and slider is visible
      if (this.autoplay && this.isVisible()) {
        this.startAutoplay();
      }
    } catch (error) {
      console.error('HeroSlider init error:', error);
    }
  }

  setupIntersectionObserver() {
    try {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              if (this.autoplay) {
                this.startAutoplay();
              }
            } else {
              this.pauseAutoplay();
            }
          });
        }, {
          threshold: 0.5
        });

        this.observer.observe(this.slider);
      }
    } catch (error) {
      console.error('IntersectionObserver setup error:', error);
    }
  }

  bindEvents() {
    try {
      // Navigation buttons
      this.prevButton?.addEventListener('click', () => this.prev());
      this.nextButton?.addEventListener('click', () => this.next());
      
      // Indicators
      this.indicators.forEach((indicator, index) => {
        indicator.addEventListener('click', () => this.goToSlide(index));
      });

      // Keyboard navigation
      this.slider.addEventListener('keydown', (e) => this.handleKeyboard(e));

      // Touch/swipe support
      this.slider.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
      this.slider.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });

      // Pause autoplay on hover (mouse users)
      this.slider.addEventListener('mouseenter', () => this.pauseAutoplay());
      this.slider.addEventListener('mouseleave', () => this.resumeAutoplay());

      // Pause autoplay on focus (keyboard users)
      this.slider.addEventListener('focusin', () => this.pauseAutoplay());
      this.slider.addEventListener('focusout', () => this.resumeAutoplay());

      // Pause autoplay when tab is not visible
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.pauseAutoplay();
        } else if (this.isVisible()) {
          this.resumeAutoplay();
        }
      });

      // Handle window resize with debouncing
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          this.updateSlidePosition(false);
        }, 250);
      });

      // Listen for reduced motion changes
      const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      motionQuery.addEventListener('change', (e) => {
        this.prefersReducedMotion = e.matches;
      });
    } catch (error) {
      console.error('Event binding error:', error);
    }
  }

  goToSlide(index, announce = true) {
    try {
      if (this.isTransitioning || index === this.currentIndex) return;
      if (index < 0 || index >= this.slides.length) return;

      this.isTransitioning = true;
      const previousIndex = this.currentIndex;
      this.currentIndex = index;
      
      this.updateSlidePosition(!this.prefersReducedMotion);
      this.updateIndicators();
      this.updateARIA();

      // Announce to screen readers
      if (announce && this.announcement) {
        this.announceSlide();
      }

      // Reset autoplay timer
      if (this.autoplay) {
        this.resetAutoplay();
      }

      // Wait for transition to complete
      const transitionTime = this.prefersReducedMotion ? 0 : this.transitionSpeed;
      setTimeout(() => {
        this.isTransitioning = false;
      }, transitionTime);
    } catch (error) {
      console.error('goToSlide error:', error);
      this.isTransitioning = false;
    }
  }

  updateSlidePosition(animate = true) {
    try {
      const offset = -this.currentIndex * 100;
      
      if (!animate || this.prefersReducedMotion) {
        this.track.style.transition = 'none';
      } else {
        this.track.style.transition = '';
      }
      
      this.track.style.transform = `translateX(${offset}%)`;
      
      if (!animate || this.prefersReducedMotion) {
        // Force reflow
        void this.track.offsetHeight;
        this.track.style.transition = '';
      }
    } catch (error) {
      console.error('updateSlidePosition error:', error);
    }
  }

  updateIndicators() {
    try {
      this.indicators.forEach((indicator, index) => {
        if (index === this.currentIndex) {
          indicator.classList.add('active');
          indicator.setAttribute('aria-current', 'true');
        } else {
          indicator.classList.remove('active');
          indicator.removeAttribute('aria-current');
        }
      });
    } catch (error) {
      console.error('updateIndicators error:', error);
    }
  }

  updateARIA() {
    try {
      // Update slide ARIA attributes
      this.slides.forEach((slide, index) => {
        if (index === this.currentIndex) {
          slide.setAttribute('aria-hidden', 'false');
          slide.removeAttribute('inert');
        } else {
          slide.setAttribute('aria-hidden', 'true');
          slide.setAttribute('inert', '');
        }
      });
    } catch (error) {
      console.error('updateARIA error:', error);
    }
  }

  announceSlide() {
    try {
      if (!this.announcement) return;
      
      const slideNumber = this.currentIndex + 1;
      const totalSlides = this.slides.length;
      this.announcement.textContent = `Slide ${slideNumber} of ${totalSlides}`;
    } catch (error) {
      console.error('announceSlide error:', error);
    }
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex);
  }

  prev() {
    const prevIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
    this.goToSlide(prevIndex);
  }

  handleKeyboard(e) {
    try {
      // Only handle if focus is within the slider
      if (!this.slider.contains(document.activeElement)) return;

      switch(e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.prev();
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.next();
          break;
        case 'Home':
          e.preventDefault();
          this.goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          this.goToSlide(this.slides.length - 1);
          break;
      }
    } catch (error) {
      console.error('handleKeyboard error:', error);
    }
  }

  handleTouchStart(e) {
    try {
      this.touchStartX = e.changedTouches[0].screenX;
    } catch (error) {
      console.error('handleTouchStart error:', error);
    }
  }

  handleTouchEnd(e) {
    try {
      this.touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe();
    } catch (error) {
      console.error('handleTouchEnd error:', error);
    }
  }

  handleSwipe() {
    try {
      const swipeThreshold = 50;
      const diff = this.touchStartX - this.touchEndX;

      if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
          // Swipe left - next slide
          this.next();
        } else {
          // Swipe right - previous slide
          this.prev();
        }
      }
    } catch (error) {
      console.error('handleSwipe error:', error);
    }
  }

  startAutoplay() {
    try {
      if (!this.autoplay || this.slides.length <= 1) return;
      if (this.autoplayTimer) return; // Already running
      
      this.autoplayTimer = setInterval(() => this.next(), this.speed);
    } catch (error) {
      console.error('startAutoplay error:', error);
    }
  }

  pauseAutoplay() {
    try {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    } catch (error) {
      console.error('pauseAutoplay error:', error);
    }
  }

  resumeAutoplay() {
    try {
      if (this.autoplay && !document.hidden && this.isVisible()) {
        this.startAutoplay();
      }
    } catch (error) {
      console.error('resumeAutoplay error:', error);
    }
  }

  resetAutoplay() {
    this.pauseAutoplay();
    this.resumeAutoplay();
  }

  isVisible() {
    try {
      if (!this.slider) return false;
      const rect = this.slider.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    } catch (error) {
      console.error('isVisible error:', error);
      return true; // Assume visible on error
    }
  }

  destroy() {
    try {
      this.pauseAutoplay();
      if (this.observer) {
        this.observer.disconnect();
      }
    } catch (error) {
      console.error('destroy error:', error);
    }
  }
}

// Initialize all hero sliders on the page
function initHeroSliders() {
  try {
    const sliders = document.querySelectorAll('[data-hero-slider]');
    sliders.forEach(slider => {
      if (!slider.heroSliderInstance) {
        slider.heroSliderInstance = new HeroSlider(slider);
      }
    });
  } catch (error) {
    console.error('initHeroSliders error:', error);
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeroSliders);
} else {
  initHeroSliders();
}

// Handle Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    try {
      const slider = event.target.querySelector('[data-hero-slider]');
      if (slider && !slider.heroSliderInstance) {
        slider.heroSliderInstance = new HeroSlider(slider);
      }
    } catch (error) {
      console.error('Shopify section load error:', error);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    try {
      const slider = event.target.querySelector('[data-hero-slider]');
      if (slider && slider.heroSliderInstance) {
        slider.heroSliderInstance.destroy();
        slider.heroSliderInstance = null;
      }
    } catch (error) {
      console.error('Shopify section unload error:', error);
    }
  });
}
